import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as core from '../../core'
import * as quiver from '../quiver'

import type { Cluster } from './cluster'
import type { Employee } from './employee'
import { Function } from './function'
import { Functionalization } from './functionalization'
import type { Organization } from './organizations'
import type { Payroll as BasePayroll } from './payroll'
import type { Project } from './project'
import type { RoomType } from './room-type'
import { ServiceCenter } from './service-center'
import { SurveyStatus } from './survey-status'

export type Record = core.common.Identified<Record.Id> &
  core.common.Named &
  core.common.Described.Optionally &
  core.common.Coded & {
    entityId: core.dms.models.Entity.Id
    // TODO: Should be removed once ancestors, entityId and id are replaced with lookup
    // lookup: Record.EntitiesLookup
    ancestors: Record.AncestorsLookup
    anomalies?: Record.Anomaly[]
    roomTypeId: RoomType.Id
    roomTypeCategory: RoomType.Category
    sqft: number
    comment?: string
    surveyStatus: SurveyStatus
    surveyStatusLite: SurveyStatus
    occupierId?: Organization.Id
    history: Record.History.Log[]
    lastUpdated?: Record.History.Log
    clusterId?: Cluster.Id
    /**
     * Missing when the survey data doesn't set it explicitly, in which case
     * it's inferred from the functionalizations (see
     * {@link Record.FunctionalUsage}), and never Joint Use
     */
    functionalizationMethodology?: Record.FunctionalUsage.Methodology
  }

export namespace Record {
  export type Id = core.dms.data.Record.Id
  export namespace Id {
    export const isId = core.dms.data.Record.Id.isId
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /** Represents the basic information of a space record which does not include survey data.  */
  export type Basic = Omit<
    Record,
    | 'anomalies'
    | 'comment'
    | 'surveyStatus'
    | 'surveyStatusLite'
    | 'history'
    | 'lastUpdated'
    | 'functionalizationMethodology'
  >
  /** Represents the survey data of a space record, which could be different for each Survey */
  export type SurveyData = Pick<
    Record,
    | 'anomalies'
    | 'comment'
    | 'surveyStatus'
    | 'surveyStatusLite'
    | 'history'
    | 'lastUpdated'
  > &
    Pick<FunctionalUsage, 'functionalizationMethodology'>

  export type Update = Partial<
    Pick<Record, 'description' | 'roomTypeId' | 'anomalies' | 'comment'>
  >

  export enum Anomaly {
    RoomDoesNotExists = 'ROOM_DOES_NOT_EXISTS',
    IncorrectFloorPlan = 'INCORRECT_FLOOR_PLAN',
    IncorrectDoorNumber = 'INCORRECT_DOOR_NUMBER',
  }
  // Needs to be before namespace is defined
  const ANOMALIES: ReadonlySet<Anomaly> = new Set(
    Object.values(Anomaly) as Anomaly[],
  )
  export namespace Anomaly {
    const LABELS: Readonly<globalThis.Record<Anomaly, string>> = {
      [Anomaly.RoomDoesNotExists]:
        quiver.resources.data.enums.anomaly.entries.roomDoesNotExists.RESOURCE
          .payload.name,
      [Anomaly.IncorrectFloorPlan]:
        quiver.resources.data.enums.anomaly.entries.incorrectFloorPlan.RESOURCE
          .payload.name,
      [Anomaly.IncorrectDoorNumber]:
        quiver.resources.data.enums.anomaly.entries.incorrectDoorNumber.RESOURCE
          .payload.name,
    }

    export const ALL: readonly Anomaly[] = Array.from(ANOMALIES)
    export const isAnomaly = TypeChecker.Factory.oneOf(ANOMALIES)
    export const assertIsAnomaly: TypeAsserter<Anomaly> =
      TypeAsserter.Factory.from(isAnomaly)

    export const getLabel = (anomaly: Anomaly): string => LABELS[anomaly]
  }

  // TODO: Should be removed once ancestors, entityId and id are replaced with lookup
  /** The name and code of each of a Space Record's ancestors, by their entity */
  export type AncestorsLookup = globalThis.Record<
    core.dms.models.Entity.Id,
    core.common.Named & core.common.Coded
  >
  export namespace AncestorsLookup {
    export const isAncestorsLookup: TypeChecker<AncestorsLookup> =
      TypeChecker.Factory.record(
        core.dms.models.Entity.Id.isId,
        TypeChecker.Factory.intersection(
          core.common.Named.isNamed,
          core.common.Coded.isCoded,
        ),
      )
    export const assertIsAncestorsLookup: TypeAsserter<AncestorsLookup> =
      TypeAsserter.Factory.from(isAncestorsLookup)
  }

  export type EntitiesLookup = globalThis.Record<
    core.dms.models.Entity.Id,
    string
  >
  export namespace EntitiesLookup {
    export const isEntitiesLookup: TypeChecker<EntitiesLookup> =
      TypeChecker.Factory.record(
        core.dms.models.Entity.Id.isId,
        TypeChecker.isString,
      )
    export const assertIsEntitiesLookup: TypeAsserter<EntitiesLookup> =
      TypeAsserter.Factory.from(isEntitiesLookup)

    export type WithName = globalThis.Record<
      core.dms.models.Entity.Id,
      core.common.Identified<Record.Id> & core.common.Named
    >
    export namespace WithName {
      export const isWithName: TypeChecker<WithName> =
        TypeChecker.Factory.record(
          core.dms.models.Entity.Id.isId,
          TypeChecker.Factory.object({
            id: core.dms.data.Record.Id.isId,
            name: TypeChecker.isString,
          }),
        )
      export const assertIsWithName: TypeAsserter<WithName> =
        TypeAsserter.Factory.from(isWithName)
    }
  }

  export interface OrganizationAssignment {
    id: OrganizationAssignment.Id
    isOccupier: boolean
    organizationLookup: Organization.Lookup
    description?: string
  }

  export namespace OrganizationAssignment {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Id = core.dms.data.Record.Id
    export namespace Id {
      export const isId = core.dms.data.Record.Id.isId
      export const assertIsId: TypeAsserter<Id> =
        TypeAsserter.Factory.from(isId)
    }

    export type WithSpaceRecordId = OrganizationAssignment & {
      spaceRecordId: Record.Id
    }
    export type Input = Omit<
      OrganizationAssignment,
      'id' | 'organizationLookup'
    > & {
      organizationId: Organization.Id
    }
  }

  /**
   * A Space Record without an occupier, along with the organization it last
   * lost as its occupier
   */
  export type Unclaimed = core.common.Identified<Record.Id> &
    core.common.Named & {
      /** The Space Record's ancestors (e.g. its building and floor) */
      ancestors: globalThis.Record<core.dms.models.Entity.Id, core.common.Named>
      roomType?: core.common.Named
      /**
       * The organization the Space Record last lost as its occupier. Missing
       * when the Space Record never lost an occupier through an edit in the
       * app, or when that organization's assignment was deleted since
       */
      previousOrganization?: core.common.Named
      /** When the Space Record lost {@link previousOrganization} as its occupier */
      unclaimedOn?: Date
    }

  export interface LabAssignment {
    id: LabAssignment.Id
    spaceRecordLookup: Partial<LabAssignment.SpaceRecordLookup>
    roomTypeId: RoomType.Id
  }

  export namespace LabAssignment {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Id = core.dms.data.Record.Id
    export namespace Id {
      export const isId = core.dms.data.Record.Id.isId
      export const assertIsId: TypeAsserter<Id> =
        TypeAsserter.Factory.from(isId)
    }

    export type WithLabServiceId = LabAssignment & {
      labServiceId: Record.Id
    }

    export type SpaceRecordLookup = Record.EntitiesLookup.WithName
  }

  export interface Filter {
    includeInaccessible?: boolean
    accessibleSpaceRecordIds?: readonly Id[]
    spaceFilter?: Filter.DimensionFilter
    orgFilter?: Filter.DimensionFilter
    roomTypeFilter?: Filter.DimensionFilter
    responsiblePersonFilter?: Filter.DimensionFilter
    sqftMax?: number
    sqftMin?: number
    surveyStatuses?: readonly SurveyStatus[]
    surveyStatusesLite?: readonly SurveyStatus[]
    roomTypeCategories?: readonly RoomType.Category[]
    lastUpdatedBy?: readonly core.admin.User.Id[]
    /** @default any methodology matches */
    functionalMethodologies?: readonly FunctionalUsage.Methodology[]
    clusterFilter?: Filter.DimensionFilter
  }
  export namespace Filter {
    // TODO: Relocate to filter.ts
    export interface DimensionFilter {
      entityId: core.dms.models.Entity.Id
      recordIds: readonly core.dms.data.Record.Id[]
    }
    export namespace DimensionFilter {
      export const isDimensionFilter: TypeChecker<DimensionFilter> =
        TypeChecker.Factory.object({
          entityId: core.dms.models.Entity.Id.isId,
          recordIds: TypeChecker.Factory.list(core.dms.data.Record.Id.isId),
        })
      export const assertIsDimensionFilter: TypeAsserter<DimensionFilter> =
        TypeAsserter.Factory.from(isDimensionFilter)
    }

    export const isFilter: TypeChecker<Filter> = TypeChecker.Factory.object({
      accessibleSpaceRecordIds: TypeChecker.Factory.optional(
        TypeChecker.Factory.list(Id.isId),
      ),
      lastUpdatedBy: TypeChecker.Factory.optional(
        TypeChecker.Factory.list(core.admin.User.Id.isId),
      ),
      orgFilter: TypeChecker.Factory.optional(
        DimensionFilter.isDimensionFilter,
      ),
      roomTypeFilter: TypeChecker.Factory.optional(
        DimensionFilter.isDimensionFilter,
      ),
      spaceFilter: TypeChecker.Factory.optional(
        DimensionFilter.isDimensionFilter,
      ),
      sqftMax: TypeChecker.Factory.optional(TypeChecker.isNumber),
      sqftMin: TypeChecker.Factory.optional(TypeChecker.isNumber),
      surveyStatuses: TypeChecker.Factory.optional(
        TypeChecker.Factory.list(SurveyStatus.isSurveyStatus),
      ),
      surveyStatusesLite: TypeChecker.Factory.optional(
        TypeChecker.Factory.list(SurveyStatus.isSurveyStatus),
      ),
    })
    export const assertIsFilter: TypeAsserter<Filter> =
      TypeAsserter.Factory.from(isFilter)
  }

  export interface Pagination {
    skip?: number
    quantity?: number
  }
  export interface Sort {
    descending?: boolean
    by?: string //
  }

  export namespace History {
    export interface Log {
      userId: core.admin.User.Id
      timestamp: Date
      action: Action
    }

    export enum Action {
      Updated = 'Updated',
      OrganizationAssignUpdated = 'Organization assignment updated',
      SignedOff = 'Signed Off',
      SignedOffLite = 'Signed Off (Lite)',
      Unlocked = 'Unlocked',
      FunctionalUsageUpdated = 'Functional Usage Updated',
      OccupantsUpdated = 'Occupants Updated',
      ResponsiblePersonsUpdated = 'Responsible Persons Updated',
      LabsUpdated = 'Labs Updated',
      ClusterAssigned = 'Cluster Assigned',
      ClusterRemoved = 'Cluster Removed',
    }
    // Needs to be before namespace is defined
    const ACTIONS: ReadonlySet<Action> = new Set(
      Object.values(Action) as Action[],
    )
    export namespace Action {
      export const ALL: readonly Action[] = Array.from(ACTIONS)
      export const isAction = TypeChecker.Factory.oneOf(ACTIONS)
      export const assertIsAction: TypeAsserter<Action> =
        TypeAsserter.Factory.from(isAction)
    }
  }

  export interface FunctionalUsage {
    functionalizations?: Functionalization[]
    functionalizationMethodology: FunctionalUsage.Methodology
    serviceCenterId?: ServiceCenter.Id
  }

  export namespace FunctionalUsage {
    export enum Methodology {
      Manual = 'Manual',
      // eslint-disable-next-line @typescript-eslint/no-shadow
      ServiceCenter = 'Service Center',
      JointUse = 'Joint Use',
      Vacant = 'Vacant',
      Unassignable = 'Unassignable',
      AutoLabBased = 'Lab Based (Auto)',
    }
    // Needs to be before namespace is defined
    const METHODOLOGIES: ReadonlySet<Methodology> = new Set(
      Object.values(Methodology) as Methodology[],
    )
    export namespace Methodology {
      /**
       * The methodologies a Space Lite record can carry. Space Lite has no way
       * to allocate functions, so it only records whether the space is in use.
       */
      export type Lite = Methodology.Manual | Methodology.Vacant

      export const ALL: readonly Methodology[] = Array.from(METHODOLOGIES)
      export const EXCLUSIVE: readonly Methodology[] = [
        Methodology.Vacant,
        Methodology.Unassignable,
      ]
      /** Returns true when the methodology supports function allocations */
      export const isAssignable = (methodology: Methodology): boolean =>
        !EXCLUSIVE.includes(methodology)

      /**
       * Infers a methodology from the functionalizations themselves, for use
       * when the survey data does not explicitly set one. Most likely this will
       * be Manual, but could be Vacant or Unassignable depending on the
       * functionalizations.
       */
      export function infer(
        functionalizations: readonly Functionalization[],
      ): Methodology {
        const isSingleFullFunctionalization =
          functionalizations.length === 1 && functionalizations[0].percent === 1

        if (!isSingleFullFunctionalization) {
          return Methodology.Manual
        }

        switch (Function.Standard.classify(functionalizations[0].functionId)) {
          case Function.Standard.Code.Unassignable:
            return Methodology.Unassignable
          case Function.Standard.Code.Vacant:
            return Methodology.Vacant
          case undefined:
            return Methodology.Manual
        }
      }
      export const isMethodology = TypeChecker.Factory.oneOf(METHODOLOGIES)
      export const assertIsMethodology: TypeAsserter<Methodology> =
        TypeAsserter.Factory.from(isMethodology)
    }

    export type Update = Pick<
      FunctionalUsage,
      'functionalizationMethodology'
    > & {
      functionalizations?: Functionalization[]
      serviceCenterId?: ServiceCenter.Id
    }
    export namespace Update {
      export const isUpdate: TypeChecker<Update> =
        TypeChecker.Factory.object<Update>({
          functionalizationMethodology: Methodology.isMethodology,
          functionalizations: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Functionalization.isFunctionalization),
          ),
          serviceCenterId: TypeChecker.Factory.optional(ServiceCenter.Id.isId),
        })
      export const assertIsUpdate: TypeAsserter<Update> =
        TypeAsserter.Factory.from(isUpdate)
    }
  }

  export interface Occupant {
    id: Occupant.Id
    employee: Employee
    payrolls: Occupant.Payroll[]
  }
  export namespace Occupant {
    export type Id = core.dms.data.Record.Id

    /**
     * The occupant's pay from a single project and organization, grouped from
     * every payroll entry of the occupant's employee that shares both
     *
     * Has no id of its own: it only exists as part of its occupant, where its
     * project and organization identify it
     */
    export interface Payroll {
      /** The payroll entries grouped into this one */
      payrollIds: BasePayroll.Id[]
      project?: Project
      organization: Organization
      /** Sum of the grouped entries' amounts */
      amount?: number
      /** Share of the employee's total pay, as a fraction */
      percent: number
      /** Merged from the grouped entries' own, weighted by their amounts */
      idcAllocations: BasePayroll.IdcAllocation[]
      /** The grouped entries assigned to the Space record */
      relevantPayrollIds: BasePayroll.Id[]
      /** How many of the grouped entries are assigned to the Space record */
      assignment: Payroll.Assignment
    }
    export namespace Payroll {
      /** How many of a payroll's grouped entries are assigned to a Space record */
      export enum Assignment {
        /** None of the grouped entries are assigned */
        None = 'None',
        /** Some, but not all, of the grouped entries are assigned */
        Partial = 'Partial',
        /** Every grouped entry is assigned */
        Full = 'Full',
      }

      /**
       * True when every one of the payroll's grouped entries is assigned — a
       * partially assigned payroll counts as not assigned
       */
      export const isFullyAssigned = ({
        assignment,
      }: Pick<Payroll, 'assignment'>): boolean => assignment === Assignment.Full
    }

    /** Occupants of many Space Records, keyed by Space Record id */
    export type Lookup = globalThis.Record<Record.Id, readonly Occupant[]>
  }

  export interface ResponsiblePerson
    extends core.common.Identified<ResponsiblePerson.Id> {
    employee: Employee
    isPrimary: boolean
  }
  export namespace ResponsiblePerson {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Id = core.dms.data.Record.Id
    export namespace Id {
      export const isId = core.dms.data.Record.Id.isId
      export const assertIsId: TypeAsserter<Id> =
        TypeAsserter.Factory.from(isId)
    }
    export interface Input {
      employeeId: Employee.Id
      /** @default false */
      isPrimary?: boolean
    }

    /** Responsible Persons of many Space Records, keyed by Space Record id */
    export type Lookup = globalThis.Record<
      Record.Id,
      readonly ResponsiblePerson[]
    >
  }
}
